<template>
  <label :class="[$style['form-input'], {[$style['form-input--error']]: meta.touched && !meta.valid }]">
    <div :id="`${id}-label`" :class="$style['form-input__label']">
      {{ label }}
    </div>
    <textarea v-model="value" v-bind="$attrs" :class="$style['form-input__input']" :aria-invalid="errors.length ? 'true' : undefined" :aria-describedby="describedBy" :aria-labelledby="label ? `${id}-label` : undefined" />
    <div v-if="hint && !errors.length" :id="`${id}-hint`" :class="$style['form-input__hint']">
      {{ hint }}
    </div>
    <div v-if="errors.length" :id="`${id}-error`" :class="$style['form-input__errors']">
      {{ errors[0] }}
    </div>
  </label>
</template>
<script setup lang="ts">
import { useField } from 'vee-validate'
const props = defineProps<{
  name: string
  modelValue?: string
  label?: string
  // A short help text under the field, replaced by the error when there is one
  hint?: string
}>()

const { value, errors, meta } = useField(() => props.name, undefined, { syncVModel: true })

const id = useId()
const describedBy = computed(() => {
  if (errors.value.length) { return `${id}-error` }
  return props.hint ? `${id}-hint` : undefined
})
</script>
<style  module>
.form-input {
  width: 100%;
}

.form-input__label {
  font-size: var(--font-size);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--spacing-s);
}

.form-input__input {
  background-color: transparent;
  border-radius: var(--border-radius);
  color: var(--text-base);
  border: 1px solid var(--border-subtle);
  padding: var(--spacing-m) var(--spacing-l);
  font-size: var(--font-size);
  line-height: var(--line-height-base);
  outline: none;
  resize: none;
  width: 100%;
}

.form-input__input:focus {
  border-color: var(--color-primary);
}

/* The public pages: amber focus, and errors in a red that reads on black */
:global(:where([data-theme="stage"])) .form-input__input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.0625rem var(--primary-color);
}

.form-input__hint {
  color: var(--text-subtle);
  font-size: var(--font-size-small);
  padding: var(--spacing) 0;
}

.form-input__errors {
  color: var(--secondary-color-50);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing) 0;
}

:global(:where([data-theme="stage"])) .form-input__errors {
  color: var(--stage-error);
}

.form-input--error .form-input__input {
  border-bottom-color: var(--secondary-color-50);
}

</style>
